import { Helmet } from "react-helmet-async";

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  type?: string;
  noindex?: boolean;
  keywords?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  ogImage?: string;
  locale?: string;
  localeAlternate?: string[];
  htmlLang?: string;
  hreflang?: { lang: string; href: string }[];
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    section?: string;
    tags?: string[];
  };
}

const SITE_NAME = "Negócio Conectado";
const BASE_URL = "https://negocioconectado.com";
const DEFAULT_DESCRIPTION =
  "Consultoria empresarial para PMEs em todo o Brasil: vendas B2B, gestão financeira, telemedicina, redução de taxas e proteção de marca.";
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.png`;

const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  canonical,
  type = "website",
  noindex = false,
  keywords,
  jsonLd,
  ogImage,
  locale = "pt_BR",
  localeAlternate,
  htmlLang,
  hreflang,
  article,
}: SEOProps) => {
  const defaultTitle = `${SITE_NAME} — Consultoria para PMEs no Brasil`;
  const withSuffix = title ? `${title} | ${SITE_NAME}` : defaultTitle;
  // Keep <title> under 60 chars: drop suffix if it pushes over the limit
  const fullTitle = title && withSuffix.length > 60 ? title : withSuffix;
  const url = canonical ? `${BASE_URL}${canonical}` : BASE_URL;
  const image = ogImage || DEFAULT_OG_IMAGE;

  const jsonLdArray = jsonLd
    ? Array.isArray(jsonLd) ? jsonLd : [jsonLd]
    : [];

  return (
    <Helmet htmlAttributes={htmlLang ? { lang: htmlLang } : undefined}>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {hreflang?.map((h) => (
        <link key={h.lang} rel="alternate" hrefLang={h.lang} href={h.href} />
      ))}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {keywords && <meta name="keywords" content={keywords} />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={locale} />
      {localeAlternate?.map((l) => (
        <meta key={l} property="og:locale:alternate" content={l} />
      ))}

      {/* Article-specific OG */}
      {article?.publishedTime && (
        <meta property="article:published_time" content={article.publishedTime} />
      )}
      {article?.section && (
        <meta property="article:section" content={article.section} />
      )}
      {article?.tags?.map((tag) => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD Structured Data */}
      {jsonLdArray.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
